const request = require('superagent');
import {getUserData, magicGradeToAmiraGrade} from "Services/API";
import {userTypes} from "Constants/constants";
import {formatGuidForSIS, isAdminUser, logSentryException} from './util';
import { setUserData } from "./API";
import networkLogger from "./networkLogger";
import { graphQLRequestIAM } from './graphQLService';

const SISurl = process.env.SIS_BASE_URL;

const GET_STUDENT = `
  query getStudent($studentId: ID!) {
    student(user_id: $studentId){
      first_name
      last_name
      grade
      metadata{
        actual_grade
        esl
        early_reader
        spanish_early_reader
        saxon
        tutorEnabled
        assessmentEnabled
        assessmentLanguageOverride
        screenerEnabled
        hasSeenFTUE
        hasSeenDialogFTUE
        hasSeenCaptureEmailModal
        tutorInSpanish
        nonverbal
        hard_to_understand
        extendAssessmentTime
        ftueStatuses {
          id
          status
        }
        badSignalAltCursorMode {
          firstActivityId
          mode
        }
        selectedTutorRig
        selectedTutorSkin
      }
    }
  }`;

const SET_FIRST_TIME = `
mutation UpdateStudentMetadata($studentId: ID!, $hasSeenFTUE: Boolean) {
  updateStudentMetadata(user_id: $studentId, hasSeenFTUE: $hasSeenFTUE) {
    hasSeenFTUE
  }
}`;

const SET_FIRST_TIME_DIALOG = `
mutation UpdateStudentMetadata($studentId: ID!, $hasSeenDialogFTUE: Boolean) {
  updateStudentMetadata(user_id: $studentId, hasSeenDialogFTUE: $hasSeenDialogFTUE) {
    hasSeenDialogFTUE
  }
}`;

const SET_HARD_TO_UNDERSTAND_FLAG  = `
mutation UpdateStudentMetadata($studentId: ID!, $hard_to_understand: Boolean) {
  updateStudentMetadata(user_id: $studentId, hard_to_understand: $hard_to_understand) {
    hard_to_understand
  }
}`;

const SET_TUTOR_IN_SPANISH  = `
mutation UpdateStudentMetadata($studentId: ID!, $tutorInSpanish: Boolean) {
  updateStudentMetadata(user_id: $studentId, tutorInSpanish: $tutorInSpanish) {
    tutorInSpanish
  }
}`;

const SET_SEEN_CAPTURE_EMAIL_MODAL  = `
mutation UpdateStudentMetadata($userId: ID!, $hasSeenCaptureEmailModal: Boolean) {
  updateStudentMetadata(user_id: $userId, hasSeenCaptureEmailModal: $hasSeenCaptureEmailModal) {
    hasSeenCaptureEmailModal
  }
}`;

const SET_FTUE_STATUSES = `
mutation UpdateStudentMetadata($studentId: ID!, $ftueStatuses: [FTUEStatusInput]) {
  updateStudentMetadata(user_id: $studentId, ftueStatuses: $ftueStatuses) {
    ftueStatuses {
      id
      status
    }
  }
}`;

// GT-326: the bad-signal cursor placement ({firstActivityId, mode}), or null to exit the student.
// Its own mutation so a write sends this one field: SIS updates only the arguments it is given.
const SET_BAD_SIGNAL_ALT_CURSOR_MODE = `
mutation UpdateStudentMetadata($studentId: ID!, $badSignalAltCursorMode: AltCursorModeStatusInput) {
  updateStudentMetadata(user_id: $studentId, badSignalAltCursorMode: $badSignalAltCursorMode) {
    badSignalAltCursorMode {
      firstActivityId
      mode
    }
  }
}`;

const UPDATE_STUDENT_METADATA = `
  mutation UpdateStudentMetadata($studentId: ID!, $esl: Boolean, $early_reader: Boolean, $spanish_early_reader: Boolean, $tutorInSpanish: Boolean, $saxon: Boolean, $assessmentLanguageOverride: String) {
    updateStudentMetadata(user_id: $studentId, esl: $esl, early_reader: $early_reader, spanish_early_reader: $spanish_early_reader, tutorInSpanish: $tutorInSpanish, saxon: $saxon, assessmentLanguageOverride: $assessmentLanguageOverride) {
      esl
      early_reader
      spanish_early_reader
      tutorInSpanish
      saxon
      assessmentLanguageOverride
    }
  }`;

const SET_TUTOR = `
  mutation SetTutor($studentId: ID!, $selectedTutorRig: String, $selectedTutorSkin: String) {
    updateStudentMetadata(user_id: $studentId, selectedTutorRig: $selectedTutorRig, selectedTutorSkin: $selectedTutorSkin) {
      selectedTutorRig
      selectedTutorSkin
    }
  }`;

const GET_CLASSROOM = `
  query Classroom($classroomId: ID!) {
    classroom(classroom_id: $classroomId) {
      id
      label
      school_id
      description
      students {
        id
        first_name
        last_name
        grade
        classroom_id
        schoolId
        metadata {
          esl
          early_reader
          spanish_early_reader
          saxon
          tutorInSpanish
          tutorEnabled
          assessmentEnabled
          screenerEnabled
        }
      }
    }
  }`;

  const GET_CLASSROOM_STUDENTS = `
  query Classroom($classroomId: ID!) {
    classroom(classroom_id: $classroomId) {
      students {
        id
        first_name
        last_name
        grade
        classroom_id
        schoolId
        metadata {
          esl
          early_reader
          spanish_early_reader
          saxon
          tutorInSpanish
          tutorEnabled
          assessmentEnabled
          assessmentLanguageOverride
          screenerEnabled
        }
      }
    }
  }`;

const GET_SCHOOL =
  `query School($schoolId: ID!) {
    school(school_id: $schoolId) {
      id
      label
      classrooms {
        id
        label
      }
    }
  }`;

const GET_DISTRICT =
  `query District($districtId: ID!) {
    district(district_id: $districtId) {
      id
      label
      schools {
        id
        label
      }
    }
  }`;

const TEACHER_SCHOOLS =
  `query TeacherSchools($user_id: ID!) {
    getTeacherSchools(user_id: $user_id){
      id
      label
    }
  }`;

const GET_PERMISSIONS =
  `query getUserPermissions($userId: ID!) {
    getUserPermissions(user_id: $userId) {
      permissionId
      scopes {
        schools
      }
    }
  }`;

export default {
  async getDistrict(districtId) {
    return (await graphqlRequest(GET_DISTRICT, {districtId})).district;
  },
  async getSchool(schoolId) {
    return (await graphqlRequest(GET_SCHOOL, {schoolId})).school;
  },
  async getClassroom(classroomId) {
    const {classroom} = await graphqlRequest(GET_CLASSROOM, { classroomId });
    classroom.students = classroom.students.map(student => transformStudent(student, classroomId));
    return classroom;
  },
  async getClassroomStudents(classroomId) {
    const {classroom} = await graphqlRequest(GET_CLASSROOM_STUDENTS, { classroomId });
    classroom.students = classroom.students.map(student => transformStudent(student, classroomId));
    return classroom.students;
  },
  async getStudent(studentId) {
    studentId = formatGuidForSIS(studentId);
    return (await graphqlRequest(GET_STUDENT, { studentId })).student;
  },
  async updateStudentMetadata(studentId, metadata){
    let existingMetadata = await this.getStudent(studentId);
    studentId = formatGuidForSIS(studentId);
    return await graphqlRequest(UPDATE_STUDENT_METADATA, { studentId, ...existingMetadata.metadata, ...metadata });
  },
  async updatePracticeConfig(studentId, metadata){
    studentId = formatGuidForSIS(studentId);
    return await graphqlRequest(UPDATE_STUDENT_METADATA, { studentId, ...metadata });
  },
  async setTutor(studentId,tutorRig,tutorSkin){
    studentId = formatGuidForSIS(studentId);
    await new Promise(resolve => setTimeout(resolve, 2000));
    return await graphqlRequest(SET_TUTOR, { studentId, selectedTutorRig: tutorRig, selectedTutorSkin: tutorSkin });
  },
  async setFTUESeen(studentId,hasSeenFTUE){
    studentId = formatGuidForSIS(studentId);
    setUserData("hasSeenFTUE",hasSeenFTUE);
    return await graphqlRequest(SET_FIRST_TIME, { studentId, hasSeenFTUE });
  },
  async setDialogFTUESeen(studentId,hasSeenDialogFTUE){
    studentId = formatGuidForSIS(studentId);
    setUserData("hasSeenDialogFTUE",hasSeenDialogFTUE);
    return await graphqlRequest(SET_FIRST_TIME_DIALOG, { studentId, hasSeenDialogFTUE });
  },
  async setFTUEStatuses(studentId, ftueStatuses) {
    studentId = formatGuidForSIS(studentId);
    setUserData("ftueStatuses", ftueStatuses);
    return await graphqlRequest(SET_FTUE_STATUSES, { studentId, ftueStatuses });
  },
  async setBadSignalAltCursorMode(studentId, badSignalAltCursorMode) {
    studentId = formatGuidForSIS(studentId);
    setUserData("badSignalAltCursorMode", badSignalAltCursorMode);
    return await graphqlRequest(SET_BAD_SIGNAL_ALT_CURSOR_MODE, { studentId, badSignalAltCursorMode });
  },
  async setHardToUnderstand(studentId,hard_to_understand){
    studentId = formatGuidForSIS(studentId);
    setUserData("hard_to_understand",hard_to_understand);
    return await graphqlRequest(SET_HARD_TO_UNDERSTAND_FLAG, { studentId, hard_to_understand });
  },
  async setTutorInSpanish(studentId,tutorInSpanish){
    studentId = formatGuidForSIS(studentId);
    //Don't set user data here so teachers don't get switched to spanish
    return await graphqlRequest(SET_TUTOR_IN_SPANISH, { studentId, tutorInSpanish });
  },
  async setSeenCaptureEmailModal(userId, hasSeenCaptureEmailModal){
    userId = formatGuidForSIS(userId);
    return await graphqlRequest(SET_SEEN_CAPTURE_EMAIL_MODAL, { userId, hasSeenCaptureEmailModal });
  },
  async getClassesInSchool(schoolId) {
    return (isAdminUser(getUserData().userType) || (getUserData().reportingRole && getUserData().reportingRole.permissionId === "SCHOOL_REPORTING_PERMISSIONS"))
      ? (await this.getSchool(schoolId)).classrooms //admins get all classes
      : await this.getTeacherClasses(getUserData().userId,schoolId); //teachers only get their assigned classes
  },
  async getTeacherClasses(teacherId,schoolId) {
    //teachers are currently not in the SIS graphql schema so we're using the SSO API for now
    const resp = await request.get(`${process.env.SSO_BASE_URL}/teacher/${teacherId}/classes`)
      .set('Content-Type', 'application/json')
      .set('correlationid', getUserData().correlationid);
    const {body} = resp;

    if (body.errors) {
      console.log(body.errors)
      throw new Error(body.errors[0].message);
    }

    let classes = body;
    if(schoolId){
      classes = classes.filter(c => !c.schoolId || c.schoolId === schoolId)
    }
    return classes.map(classroom => {
      return {id: classroom.id, label: classroom.name}
    });
  },
  async getTeacherSchools(teacherId){
    return (await graphqlRequest(TEACHER_SCHOOLS, {user_id: teacherId})).getTeacherSchools;
  },
  //TODO: move the rest of the SIS calls from Reports/services/roster.js into this file and refactor accordingly
  //TODO: refactor magic calls into a separate service
  async getUserInfoByUserId(userId){
    let formattedUserId = formatGuidForSIS(userId);
    const url = "https://api.getmagicbox.com/services//user/v1.0/user/" + formattedUserId;
    return fetch(url,{method: "GET"})
    .then((resp) => resp.json())
    .then(function(resp){
      return Promise.resolve(resp.data);
    })
    .catch(err => Promise.reject(err));
  },
  async getDistrictAndSchoolsByUserName(userName){
    const url = "https://api.getmagicbox.com/services//user/v1.0/userdetails/" + userName;
    return fetch(url,{method: "GET"})
    .then((resp) => resp.json())
    .then(function(resp){
      if(resp.data.userType === userTypes.DISTRICT_ADMIN){
        const getSchoolsUrl = "https://api.getmagicbox.com/services//districts/v1.0/" + resp.data.schoolId + "/schools";
        return fetch(getSchoolsUrl,{method: "GET"})
        .then((school) => school.json())
        .then(function(schools){
          let returnSchools = schools.data.map((item)=>{
            return({label: item.schoolName, id: item.schoolId});
          });
          let returnDistrict = {label: schools.districtName, id: resp.data.schoolId};
          return Promise.resolve({schools: returnSchools, district: returnDistrict});
        })
        .catch((err)=>Promise.reject(err));
      }else{
        const userSchoolID = (resp.data.userType === userTypes.SCHOOL_ADMIN) ? resp.data.school.schoolId  : resp.data.schoolId;
        const getSchoolUrl =  "https://api.getmagicbox.com/services//school/v1.0/" + userSchoolID;
        return fetch(getSchoolUrl,{method: "GET"})
        .then((school) => school.json())
        .then(function(schools){
          const returnSchool = {
            label: schools.data.schoolName,
            id: schools.data.schoolId,
          };
          const returnDistrict = {
            label: schools.data.districtInfo[0].districtname,
            id: schools.data.districtInfo[0].districtId,
          }
          return Promise.resolve({schools: [returnSchool], district: returnDistrict});
        })
        .catch((err)=>Promise.reject(err));
      }
    })
    .catch(err => Promise.reject(err));
  },
  async getSchoolsAndDistricts(userData,authData,userName,userId,userType,isSSO) {
    if(isSSO && userData.attributes['custom:sisId']) {
      // If a subdistrict was provided, get its information (so we can use it to determine what schools the user can see)
      const districtId = ((userType === userTypes.DISTRICT_ADMIN) && authData.attributes.subdistrictId) ? authData.attributes.subdistrictId : authData.attributes.districtId;

      const requests = await Promise.all([
        this.getDistrict(districtId),
        this.getPermissions(userId)
      ]);

      //For all SSO users (all user types), district ID is passed as a query string param.
      //School ID query string param is only passed for school admins and teachers
      let district = requests[0];
      let permissions = requests[1];
      const reportingRole = permissions ? permissions.find(p => p.permissionId === "SCHOOL_REPORTING_PERMISSIONS") : null;

      const schoolId = authData.attributes.schoolId;
      let schools = district ? district.schools : [];

      //If the user has the school reporting permission, use the list of schools in the permission
      if(reportingRole && (reportingRole.permissionId === "SCHOOL_REPORTING_PERMISSIONS") && (reportingRole.scopes && reportingRole.scopes.schools && reportingRole.scopes.schools.length > 0)){
        //If the user has access to all schools, use the list of schools in the district
        if(reportingRole.scopes.schools[0] === '*'){
          schools = district.schools;
        }else{
          //Filter down to the schools where the school IDs match the school IDs in the permissions
          schools = schools.filter(school => reportingRole.scopes.schools.includes(school.id.toString()));
        }
      }else{
        //If the user is a district admin, use the list of schools in the district
        schools = userType === userTypes.DISTRICT_ADMIN ? schools
        : schools.filter(school => school.id == schoolId);

        //If this user is a teacher, override the schools with its value
        if(userType === userTypes.TEACHER){
          try{
            schools = await this.getTeacherSchools(userId,schoolId);
          }catch(err){
            console.log("Error getting schools, using default school passed at login",err);
            logSentryException(new Error('Teacher schools fetch failure'), 'Error getting teacher schools, using default school passed at login', { originalError: err?.message, originalStack: err?.stack });
          };
        }
      }
      console.log("REPORTING ROLE",reportingRole);
      return Promise.resolve({
        schools,
        district: {id: authData.attributes.districtId, label: district.label},
        subdistrictId: authData.attributes.subdistrictId,
        permissions,
        reportingRole
      });
    }else{
      if(userData.attributes['custom:sisId']){
        //Non-SSO (Magic) users will have a sisId attribute in the Cognito user
        return(this.getDistrictAndSchoolsByUserName(userName));
      }else{
        //If non-SSO and not Magic, get the information from our Cloud Directory API
        let url = process.env.SSO_BASE_URL+"/sis/admin/"+userId+"/district";
        let reqHeaders = {};
        return fetch(url,{
          method: "GET",
          headers: reqHeaders
        })
        .then((resp)=>resp.json())
        .then((resp)=>{
          let schools = resp.schools.map((school)=>{
            return {id: parseInt(school.schoolId),label: school.schoolName};
          });
          console.log(JSON.stringify(schools));
          let returnObject = {
            schools: schools,
            district: {id: parseInt(resp.districtId), label: resp.districtName}
          };

          return Promise.resolve(returnObject);
        });
      }
    }
  },
  async getPermissions(userId){
    return (await graphqlRequest(GET_PERMISSIONS, {userId})).getUserPermissions;
  }
}

function transformStudent(student, classroomId){
  return {
    ...student,
    school: student.schoolId,
    classroom: classroomId,
    grade: getUserData().skipMagic ? parseInt(student.grade) : magicGradeToAmiraGrade(student.grade)
  }
}

async function graphqlRequest(query, variables) {
  const startTime = Date.now();
  try {
    const response = await graphQLRequestIAM(SISurl, query, variables, 3);
    const duration = Date.now() - startTime;

    if (response.errors) {
      console.log(response.errors);
      networkLogger.logGraphQLError(
        'StudentInformationService',
        SISurl,
        null,
        response.errors,
        duration
      );
      const graphqlError = new Error(response.errors[0].message);
      graphqlError.isGraphQLError = true;
      throw graphqlError;
    }

    return response.data;
  } catch (error) {
    if (!error.isGraphQLError) {
      networkLogger.logGraphQLError(
        'StudentInformationService',
        SISurl,
        null,
        error,
        Date.now() - startTime
      );
    }
    throw error;
  }
}
